import { useGameEvents } from "@/game/hooks/useGameEvents";
import { getEmote, type Emote } from "@/lib/emotes";
import { API, Constants } from "@nextgammon/shared";
import { useEffect, useRef, useState } from "react";
import "../styles/emotes.css";

interface EmoteDisplayProps {
  playerId: string;
  gameId: string;
  isMuted: boolean; // From parent component's mute state
}

export function EmoteDisplay({ playerId, gameId, isMuted }: EmoteDisplayProps) {
  const [currentEmote, setCurrentEmote] = useState<Emote | null>(null);
  const { subscribe } = useGameEvents(gameId);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    // Subscribe to emote events
    const unsubscribe = subscribe(
      API.Game.SSEEventType.EMOTE_SENT,
      (data: API.Game.EmoteSentEvent) => {
        if (data.playerId === playerId && !isMuted) {
          // Clear any existing timeout
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }

          // Get emote (synchronous)
          const emote = getEmote(data.emoteId);
          if (emote) {
            setCurrentEmote(emote);

            // Auto-remove after animation completes
            timeoutRef.current = setTimeout(() => {
              setCurrentEmote(null);
            }, Constants.EMOTE_COOLDOWN_MS);
          }
        }
      }
    );

    return () => {
      unsubscribe?.();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [playerId, gameId, subscribe]);

  if (!currentEmote || isMuted) return null;

  return (
    <div className="emote-animation">
      <img src={currentEmote.img} alt={currentEmote.id} title={currentEmote.id} />
    </div>
  );
}
